import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Hero from "@/components/Hero";
import Marquee from "@/components/Marquee";
import CostComparison from "@/components/CostComparison";
import TrustIndicators from "@/components/TrustIndicators";
import WhyStudyFrance from "@/components/WhyStudyFrance";
import ServicesGrid from "@/components/ServicesGrid";
import WorkWhileStudy from "@/components/WorkWhileStudy";
import AloneVsFrancePath from "@/components/AloneVsFrancePath";
import FAQSection from "@/components/FAQSection";

const stages = [
  { num: "01", title: "Discover", to: "/discover", copy: "Learn what studying in France really costs and what life there looks like.", img: "/stages/discover.jpg" },
  { num: "02", title: "Plan", to: "/plan", copy: "Compare universities, cities, budgets, and programs that fit your goals.", img: "/stages/plan.jpg" },
  { num: "03", title: "Apply", to: "/apply", copy: "Stay on top of applications, deadlines, and required documents.", img: "/stages/apply.jpg" },
  { num: "04", title: "Prepare", to: "/prepare", copy: "Follow step-by-step guides for visas, housing, banking, insurance, and more.", img: "/stages/prepare.jpg" },
  { num: "05", title: "Thrive", to: "/thrive", copy: "Settle into life in France with community resources, internships, and practical advice.", img: "/stages/thrive.jpg" },
];

const testimonials = [
  { quote: "I had never heard of two of the universities FrancePath recommended. One ended up offering exactly the program I wanted.", who: "Maya · Chicago → Bordeaux" },
  { quote: "The CAF instructions alone paid for the concierge fee three times over. We would have missed the deadline without them.", who: "Parents of Ethan · Denver → Lyon" },
  { quote: "I moved in on day one with a French bank account, insurance, and a SIM already sorted. Not a single line at a prefecture.", who: "Jules · Boston → Paris" },
];

const fade = (i = 0) => ({
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] } },
});

export default function Home() {
  return (
    <div data-testid="page-home">
      <Hero />

      {/* Trust indicators strip */}
      <TrustIndicators />

      {/* Marquee band */}
      <div className="bg-[#0d1b3e] text-[#f5f1e8] py-8 border-y border-[#b8892b]/30">
        <Marquee items={["Bonjour", "Étudier", "S'installer", "Réussir", "Faire sa vie", "Habiter Paris"]} />
      </div>

      {/* Why Study in France */}
      <WhyStudyFrance />

      {/* How it works, 3 step bento */}
      <section className="bg-[#f5f1e8] py-24 md:py-32" data-testid="how-it-works">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fade(0)}
            className="flex items-center gap-3 mb-8">
            <span className="rule-gold" /><span className="eyebrow">02 · How it works</span>
          </motion.div>
          <motion.h2 initial="hidden" whileInView="show" viewport={{ once: true }} variants={fade(1)}
            className="serif text-5xl md:text-6xl max-w-3xl leading-[0.98] tracking-tight">
            Three simple steps, <em className="italic text-[#b8892b]">from your bedroom in the U.S. to your new home in France.</em>
          </motion.h2>

          <div className="mt-16 grid md:grid-cols-12 gap-6">
            {[
              { n: "01", t: "Tell us your goals", d: "Answer four quick questions about your goals, budget, preferred city, and French level." },
              { n: "02", t: "Get matched", d: "We compare your profile against our curated university database and recommend the best matches, with clear explanations." },
              { n: "03", t: "Get guided all the way to arrival", d: "Track applications, complete your paperwork, and prepare for life in France with one personalized roadmap." },
            ].map((s, i) => (
              <motion.div key={s.n} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fade(i + 2)}
                className={`bg-white border border-[#0d1b3e]/10 p-8 md:p-10 flex flex-col justify-between min-h-[280px] ${i === 1 ? "md:col-span-5" : "md:col-span-4"} ${i === 2 ? "md:col-span-3" : ""}`}>
                <div className="chapter-num">{s.n}</div>
                <div>
                  <h3 className="serif text-2xl md:text-3xl text-[#0d1b3e] mt-6">{s.t}</h3>
                  <p className="text-[#4a5570] mt-3 text-sm leading-relaxed">{s.d}</p>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="mt-12">
            <Link to="/advisor" className="btn-sharp btn-sharp-gold" data-testid="how-it-works-cta">
              Start Your Free Plan <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* Cost comparison */}
      <CostComparison />

      {/* Work While You Study */}
      <WorkWhileStudy />

      {/* Services grid */}
      <ServicesGrid />

      {/* Meet Rose teaser */}
      <section className="bg-[#f5f1e8] py-24 md:py-32" data-testid="meet-rose">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10">
          <motion.div
            initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.7 }}
            className="grid md:grid-cols-12 gap-8 md:gap-14 items-center"
          >
            <div className="md:col-span-5">
              <Link to="/discover#founder-section" data-testid="meet-rose-portrait-link" className="block group">
                <div className="relative aspect-[4/5] overflow-hidden bg-[#0d1b3e] border border-[#0d1b3e]/10">
                  <img
                    src="/founder/rose.png"
                    alt="Rose Marcelin, founder of FrancePath"
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-105"
                    loading="eager"
                  />
                  <div className="absolute top-4 left-4 bg-[#b8892b] text-white text-[10px] uppercase tracking-[0.22em] px-2 py-1">
                    Founder
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0d1b3e]/60 via-transparent to-transparent" />
                  <div className="absolute bottom-4 left-4 right-4 text-white">
                    <div className="eyebrow !text-[#b8892b]">Rose Marcelin</div>
                    <div className="serif text-xl md:text-2xl mt-1 leading-tight">Founder of FrancePath</div>
                  </div>
                </div>
              </Link>
            </div>
            <div className="md:col-span-7">
              <div className="flex items-center gap-3 mb-4">
                <span className="rule-gold" /><span className="eyebrow">Meet the founder</span>
              </div>
              <h2 className="serif text-4xl md:text-6xl leading-[0.98] tracking-tight text-[#0d1b3e]">
                A guide from someone who <em className="italic text-[#b8892b]">actually lived both sides</em>.
              </h2>
              <p className="mt-6 text-lg text-[#4a5570] leading-relaxed max-w-2xl">
                Rose grew up between France and Florida. When she returned to France as an adult, she discovered that even someone born there could struggle to navigate the system. FrancePath is the guide she wishes she'd had, a single place that explains every step of studying, moving, and building a life in France.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/discover#founder-section" className="btn-sharp btn-sharp-gold" data-testid="meet-rose-cta">
                  Read Rose's story
                </Link>
                <a href="https://wa.me/33618797689" target="_blank" rel="noreferrer" className="btn-sharp btn-sharp-outline" data-testid="meet-rose-whatsapp">
                  Ask Rose anything
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Journey stage preview */}
      <section className="bg-[#0d1b3e] text-white py-24 md:py-32" data-testid="stage-preview">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10">
          <div className="flex items-center gap-3 mb-8">
            <span className="rule-gold" /><span className="eyebrow text-[#b8892b]">Five stages · one path</span>
          </div>
          <h2 className="serif text-5xl md:text-7xl max-w-4xl leading-[0.98] tracking-tight">
            The journey, <em className="italic text-[#b8892b]">chapter by chapter</em>.
          </h2>
          <p className="mt-6 text-white/60 max-w-xl text-lg leading-relaxed">
            We didn't organise the site by feature. We organised it by where you are, because "housing" means something
            different in April than it does in August.
          </p>

          <div className="mt-16 grid md:grid-cols-5 gap-4">
            {stages.map((s, i) => (
              <motion.div key={s.num} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }} transition={{ duration: 0.6, delay: i * 0.08 }}
                whileHover={{ y: -6 }}
                className="group border border-white/10 bg-white/[0.02] hover:bg-white/[0.05] transition-colors"
                data-testid={`stage-card-${s.title.toLowerCase()}`}
              >
                <Link to={s.to} className="block">
                  <div className="aspect-[4/5] overflow-hidden relative">
                    <img src={s.img} alt={s.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-110" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0d1b3e] via-[#0d1b3e]/20 to-transparent" />
                    <div className="absolute top-4 left-4 text-[#b8892b] font-semibold text-sm">{s.num}</div>
                  </div>
                  <div className="p-5">
                    <div className="flex items-center justify-between">
                      <h3 className="serif text-2xl">{s.title}</h3>
                      <ArrowRight size={16} className="text-[#b8892b] transition-transform group-hover:translate-x-1" />
                    </div>
                    <p className="text-sm text-white/60 mt-2 leading-relaxed">{s.copy}</p>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FrancePath vs Alone */}
      <AloneVsFrancePath />

      {/* Testimonials */}
      <section className="bg-[#f5f1e8] py-24 md:py-32" data-testid="testimonials">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10">
          <div className="flex items-center gap-3 mb-10">
            <span className="rule-gold" /><span className="eyebrow">Voices</span>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((t, i) => (
              <motion.blockquote key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ duration: 0.6, delay: i * 0.08 }}
                className="border-t border-[#0d1b3e]/20 pt-6">
                <div className="serif text-2xl md:text-3xl leading-tight text-[#0d1b3e]">"{t.quote}"</div>
                <div className="mt-4 text-xs uppercase tracking-[0.2em] text-[#b8892b]">{t.who}</div>
              </motion.blockquote>
            ))}
          </div>
        </div>
      </section>

      {/* Trust bar */}
      <section className="bg-[#f5f1e8] border-t border-[#0d1b3e]/10 py-14 md:py-16" data-testid="trust-bar">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10">
          <div className="flex items-center gap-3 mb-8">
            <span className="rule-gold" /><span className="eyebrow">Why families trust FrancePath</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-8 md:gap-6">
            {[
              ["20+", "French universities in our database"],
              ["11", "chapters of paperwork guides"],
              ["18", "checklist items in the tracker"],
              ["< 1 day", "average response time"],
              ["Both", "systems, actually lived"],
            ].map(([n, l], i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.06 }}
                data-testid={`trust-stat-${i}`}
              >
                <div className="serif text-3xl md:text-4xl text-[#0d1b3e] leading-none">{n}</div>
                <div className="text-[11px] uppercase tracking-[0.2em] text-[#4a5570] mt-2 leading-snug">{l}</div>
              </motion.div>
            ))}
          </div>
          <div className="mt-8 text-xs text-[#4a5570] italic max-w-2xl leading-relaxed">
            Built by someone who has lived both systems. Campus France references and university partnerships are added as they are formalised.
          </div>
        </div>
      </section>

      {/* FAQ */}
      <FAQSection />

      {/* Secondary CTA band */}
      <section className="bg-[#0d1b3e] text-white py-20" data-testid="secondary-cta">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 flex flex-col md:flex-row md:items-end justify-between gap-8">
          <div>
            <div className="eyebrow text-[#b8892b] mb-3">Your journey starts here</div>
            <h3 className="serif text-4xl md:text-6xl max-w-3xl leading-[1]">
              Your journey to France starts here.
            </h3>
            <p className="mt-4 text-white/70 max-w-2xl leading-relaxed">
              Whether you're just beginning your search or ready to apply, FrancePath will guide you from choosing a university to settling into your new life in France. We usually reply within one business day.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link to="/advisor" className="btn-sharp btn-sharp-gold" data-testid="final-cta-primary">
              Get My Free Study Plan
            </Link>
            <a href="https://wa.me/33618797689" target="_blank" rel="noreferrer" className="btn-sharp !bg-transparent !text-white !border-white/40 hover:!bg-white hover:!text-[#0d1b3e]" data-testid="cta-whatsapp">
              Book a free consultation
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
